<template>
  <div>
    <div class="breadcrumb-area pos-relative w-100">
      <h1>Quản lý thông tin giao hàng</h1>
      <div class="float-right btn-group">
        <DraggableTable
          :naming="$route.name"
          v-model="fieldsTable"
          @change="filterTable = $event"
        />
        <b-button
          variant="outline-secondary"
          @click="handleExport"
          class="font-medium f12 no-padding"
        >
          <i class="bx bx-download"></i> Xuất file
        </b-button>
      </div>
    </div>
    <div class="p-10 card pt-15 b-t-1-869fb229">
      <FilterShippingForm ref="filterShippingFormRef" @change="handleFilter" />
      <div class="card-body">
        <div class="tab-content" id="nav-tabContent">
          <div class="p-10">
            <span><span class="text-muted">Tổng số</span>: {{ total }}</span>
            <div class="table-responsive">
              <b-table
                :busy.sync="isTableLoading"
                sticky-header="600px"
                hover
                :items="dataList"
                :fields="filterTable"
                bordered
                show-empty
                responsive
                class="nowrap-table"
                style="min-width: 100%"
              >
                <template #table-busy>
                  <div class="my-2 text-center">
                    <b-spinner class="align-middle"></b-spinner>
                  </div>
                </template>
                <template #empty>
                  <div class="text-center text-info my-2">Không có dữ liệu</div>
                </template>
                <template #cell(index)="data">
                  {{ getIndex(data.index) }}
                </template>
                <template #cell(MCInfor)="data">
                  <p><span class="text-muted">Tên: </span>{{ data.item.merchantName }}</p>
                  <p>
                    <span class="text-muted">Email/SĐT: </span>
                    {{ data.item.email ?? data.item.mobile }}
                  </p>
                  <p>
                    <span class="text-muted">Địa chỉ: </span>
                    <span>{{ `${data.item.address}, ` }}</span>
                    <span>{{ `${data.item.district ?? data.item.ward} - ` }}</span>
                    <span>{{ data.item.city }}</span>
                  </p>
                </template>
                <template #cell(DeviceInfor)="data">
                  <div
                    v-for="(serials, deviceName) in data.item.devices"
                    :key="deviceName"
                    class="mb-2"
                  >
                    <p><span class="text-muted">Tên thiết bị: </span>{{ deviceName }}</p>
                    <p>
                      <span class="text-muted">Serial: </span>
                      <!-- Chỉ hiển thị showMaxDivice serial đầu tiên -->
                      <span v-for="(serial, idx) in serials.slice(0, showMaxDivice)" :key="serial">
                        {{ serial }}<span v-if="idx < serials.length - 1">, </span>
                      </span>

                      <!-- Nếu số lượng serial > showMaxDivice thì thêm dấu ... -->
                      <span v-if="serials.length > showMaxDivice">...</span>
                    </p>
                  </div>
                </template>
                <template #cell(handoverType)="data">
                  <b-badge :variant="data.item.feeType === 'ONLINE' ? 'success' : 'warning'">
                    {{ data.item.feeType === 'ONLINE' ? 'Online' : 'Offline' }}
                  </b-badge>
                </template>
                <template #cell(carrierName)="data">
                  {{ mapTextCarrierName[data.item.carrierName] ?? '' }}
                </template>
                <template #cell(shippingStatus)="data">
                  <b-badge :variant="mapVariantShippingStatus[data.item.shippingStatus]">
                    {{ mapTextShippingStatus[data.item.shippingStatus] }}
                  </b-badge>
                </template>
                <template #cell(deliveryTime)="data">
                  {{ data.item.deliveryTime | formatDate }}
                </template>
                <template #cell(fee)="data">
                  {{ data.item.fee | fomatCurrency }}
                </template>
                <template #cell(approvedStatus)="data">
                  <b-badge :variant="mapVariantApprovedStatus[data.item.approvedStatus]">
                    {{ mapTextApprovedStatus[data.item.approvedStatus] }}
                  </b-badge>
                </template>
                <template #cell(action)="data">
                  <button class="btn btn-sm btn-info" @click="viewDetail(data.item.leadId)">
                    Chi tiết
                  </button>
                </template>
              </b-table>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex justify-content-end">
        <b-form-group class="mb-0" style="padding: 17px">
          <b-form-select
            v-model="fieldsSearch.limit"
            :options="pageOptions"
            @change="onItemsPerPageChange"
          ></b-form-select>
        </b-form-group>
        <b-pagination
          class="mr-2 fl-right"
          v-model="fieldsSearch.page"
          :total-rows="total"
          :per-page="fieldsSearch.limit"
          @change="onChangePage"
        ></b-pagination>
      </div>
    </div>
    <VueLoadingOverlay :active.sync="showLoading" :is-full-page="true"> </VueLoadingOverlay>
  </div>
</template>

<script>
  import Config from '@/thirdParty/config'
  import cloneDeep from 'lodash/cloneDeep'
  import { showMess } from '@/config/utils'
  import { LeadsService } from '@/services/lead.service'

  import FilterShippingForm from './components/FilterShippingForm.vue'

  const fieldsTable = [
    { key: 'index', label: 'STT', class: 'text-center' },
    {
      key: 'MCInfor',
      label: 'Thông tin MC',
      thStyle: { 'min-width': '250px' },
      tdClass: 'break-word',
    },
    {
      key: 'DeviceInfor',
      label: 'Thiết bị gửi MC',
      thStyle: { 'min-width': '250px' },
      tdClass: 'break-word',
    },
    { key: 'handoverType', label: 'Hình thức bàn giao', class: 'text-center' },
    { key: 'carrierName', label: 'Tên đơn vị vận chuyển' },
    { key: 'trackingCode', label: 'Mã đơn vị' },
    { key: 'shippingStatus', label: 'Trạng thái giao hàng', class: 'text-center' },
    { key: 'deliveryTime', label: 'Ngày giao' },
    { key: 'fee', label: 'Giá vận chuyển', class: 'text-right' },
    { key: 'approvedStatus', label: 'Trạng thái duyệt', class: 'text-center' },
    { key: 'action', label: 'Thao tác', class: 'text-center' },
  ]

  const searchFieldDefault = Object.freeze({
    page: 1,
    limit: 20,
  })

  const pageOptions = [
    { value: 20, text: '20/trang' },
    { value: 50, text: '50/trang' },
    { value: 100, text: '100/trang' },
    { value: 200, text: '200/trang' },
  ]

  export default {
    components: {
      FilterShippingForm,
    },
    data() {
      return {
        isTableLoading: false,
        showLoading: false,
        dataList: [],
        total: 2,
        fieldsTable,
        filterTable: fieldsTable,
        Config,
        fieldsSearch: cloneDeep(searchFieldDefault),
        pageOptions,
        showMaxDivice: 10,
      }
    },
    computed: {
      mapTextCarrierName() {
        return Config.listCarrierName.reduce((acc, item) => {
          acc[item.id] = item.text
          return acc
        }, {})
      },
      mapTextShippingStatus() {
        return Config.ShippingStatus.reduce((acc, item) => {
          acc[item.id] = item.text
          return acc
        }, {})
      },
      mapVariantShippingStatus() {
        return Config.ShippingStatus.reduce((acc, item) => {
          acc[item.id] = item.variant
          return acc
        }, {})
      },
      mapTextApprovedStatus() {
        return Config.ShippingApprovedSatus.reduce((acc, item) => {
          acc[item.id] = item.text
          return acc
        }, {})
      },
      mapVariantApprovedStatus() {
        return Config.ShippingApprovedSatus.reduce((acc, item) => {
          acc[item.id] = item.variant
          return acc
        }, {})
      },
    },
    async created() {
      await this.fetchData()
    },
    methods: {
      async onChangePage(page) {
        this.fieldsSearch.page = page
        await this.fetchData()
      },
      async onItemsPerPageChange() {
        this.fieldsSearch.page = 1
        await this.fetchData()
      },
      async handleFilter(payload) {
        this.fieldsSearch = {
          page: 1,
          limit: this.fieldsSearch.limit,
          ...payload,
        }
        await this.fetchData()
      },
      getIndex(index) {
        return (this.fieldsSearch.page - 1) * this.fieldsSearch.limit + index + 1
      },

      viewDetail(id) {
        window.open(`/management-shipping/${id}`)
      },
      async handleExport() {
        const body = this.$refs.filterShippingFormRef.params()
        try {
          await LeadsService.exportDataShipping(body)
        } catch (error) {
          showMess(
            this,
            'Error',
            error.response?.data?.message || error.response?.message || error.message,
            'danger',
            'b-toaster-top-right',
            3000
          )
        }
      },
      async fetchData() {
        const body = { ...this.fieldsSearch }
        try {
          this.isTableLoading = true
          const res = await LeadsService.getListLeadShipping(body)
          if (res.code === 1000) {
            this.total = res.data.totalElements
            this.dataList = res.data.content
          } else {
            showMess(this, 'Error', res.message, 'danger', 'b-toaster-top-right', 3000)
          }
        } catch (error) {
          showMess(
            this,
            'Error',
            error.response?.data?.message || error.response?.message || error.message,
            'danger',
            'b-toaster-top-right',
            3000
          )
        } finally {
          this.isTableLoading = false
        }
      },
    },
  }
</script>

<style scoped>
  .nowrap-table {
    width: 100%;
    margin-bottom: 0;
  }

  .nowrap-table ::v-deep td {
    white-space: nowrap;
    padding: 0.5rem;
  }

  .nowrap-table ::v-deep td.break-word {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
  }

  .nowrap-table ::v-deep th {
    white-space: nowrap !important;
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .nowrap-table ::v-deep p {
    margin-bottom: 0.25rem;
  }

  .nowrap-table ::v-deep p:last-child {
    margin-bottom: 0;
  }

  .action-h {
    height: 41px;
  }
</style>
